import React from 'react';
import { Container } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';
import {} from 'prosemirror-markdown';

import {
  GetFormMedia,
  GetResourceValue,
  GetTaskContentById
} from '../../../utils/game-document';
import Answer from './answers/answer';
import { TaskEntity } from '../../../types/game-document/entities';
import { GameDocumentContext } from '../../../contexts/game-document';
import { isContentHtmlEmpty } from '../../../utils/game-document/html-content';
import { TaskPremessageDialog } from './task-premessage-dialog';
import ReactMarkdown from 'react-markdown';
import { Gallery } from '../../gallery';
import { processMediaTags } from '../../../utils/media-tag-converter';

interface TaskPreviewDialogProps {
  selectedTask?: TaskEntity;
  toggle: () => void;
}

export const TaskPreviewDialog: React.FC<TaskPreviewDialogProps> = ({
  selectedTask,
  toggle
}) => {
  const [state] = React.useContext(GameDocumentContext);
  const [currentFormIndex, setCurrentFormIndex] = React.useState(0);
  const [hasPremessage, setHasPremessage] = React.useState(false);
  const [hasIntro, setHasIntro] = React.useState('');
  const [selectedTab, setSelectedTab] =
    React.useState<string>('Task information');

  const selectedTaskContent = GetTaskContentById(
    state.gameDocument,
    selectedTask?.taskContentId!
  );
  const formQuestions = selectedTaskContent?.forms ?? [];

  const answerType = React.useMemo(() => {
    if (formQuestions.length === 0) return undefined;
    return formQuestions[currentFormIndex].type;
  }, [formQuestions, currentFormIndex]);

  const formMedia = React.useMemo(() => {
    if (state.gameDocument && formQuestions.length > 0)
      return GetFormMedia(state.gameDocument, formQuestions[currentFormIndex]);

    return [];
  }, [state.gameDocument, formQuestions, currentFormIndex]);

  const handleNavigation = (navigation: 'prev' | 'next') =>
    setCurrentFormIndex((prev) =>
      navigation === 'prev' ? (prev -= 1) : (prev += 1)
    );

  React.useEffect(() => {
    if (!selectedTaskContent || !state.gameDocument) return;

    let cancelled = false;

    const processContent = async (resId?: string): Promise<string> => {
      if (!resId) return '';

      const rawContent = GetResourceValue(state.gameDocument!, resId);
      if (isContentHtmlEmpty(rawContent)) return '';

      // only fetch media when there is actual content
      return processMediaTags(rawContent);
    };

    const load = async () => {
      try {
        const [premessageContent, introContent] = await Promise.all([
          processContent(selectedTaskContent.preMessageResId),
          processContent(selectedTaskContent.contentResId)
        ]);

        if (cancelled) return;

        setHasPremessage(!!premessageContent);
        setHasIntro(introContent);
      } catch (error) {
        console.error('Failed to process media tags', error);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [state.gameDocument, selectedTaskContent]);

  return hasPremessage ? (
    <TaskPremessageDialog
      taskContent={selectedTaskContent}
      toggle={toggle}
      onContinue={() => {
        hasIntro || formQuestions.length > 0
          ? setHasPremessage(false)
          : toggle();
      }}
    />
  ) : hasIntro || formQuestions.length > 0 ? (
    <div className={'task-content__mask'}>
      <Container className={'task-content px-0'} fluid>
        <div className={'preview-title'}>
          <h2 className={'fw-bold p-0 m-0 text-dark'}>Preview</h2>
          <div
            className={`d-flex justify-content-center align-items-center cursor-pointer text-decoration-none`}
            onClick={toggle}>
            <span className={'material-symbols-outlined fw-bold'}>close</span>
          </div>
        </div>

        <div className={'content__header'}>
          <h1 className={'fw-bold p-0 m-0 text-white'}>
            {GetResourceValue(state.gameDocument!, selectedTask?.titleResId!)}
          </h1>
        </div>

        <div className={'d-flex gap-4 border-bottom px-3 pt-3'}>
          {[
            ...(hasIntro ? ['Task information'] : []),
            ...(formQuestions.length > 0 ? ['Questions'] : [])
          ].map((tab) => (
            <div
              key={tab}
              style={{
                cursor: 'pointer',
                ...(selectedTab === tab && {
                  color: '#003A64',
                  fontWeight: 'bold',
                  paddingBottom: '5px',
                  padding: '0 5px 8px',
                  borderBottom: '3px solid #003A64'
                })
              }}
              onClick={() => {
                setSelectedTab(tab);
                if (tab === 'Questions' && currentFormIndex < 0)
                  setCurrentFormIndex(0);
              }}>
              {tab}
            </div>
          ))}
        </div>

        {selectedTab === 'Task information' && (
          <>
            <div className={'content__body'}>
              <div className={'content__intro'}>
                <div>
                  {selectedTaskContent?.bodyType === 'HTML' ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: hasIntro
                      }}
                    />
                  ) : (
                    <ReactMarkdown>{hasIntro ?? ''}</ReactMarkdown>
                  )}
                </div>
              </div>
            </div>

            <div className={'content__footer'}>
              <div className={'content border-top'}>
                <div
                  className={`align-items-center d-flex justify-content-end`}>
                  <Button
                    themeColor={'success'}
                    onClick={() =>
                      formQuestions.length > 0
                        ? setSelectedTab('Questions')
                        : toggle()
                    }>
                    <span className={'d-flex align-items-center'}>
                      <span>Continue</span>
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {selectedTab === 'Questions' && (
          <>
            <div className={'content__body'}>
              <div className={`d-flex justify-content-center`}>
                <div
                  className={`d-flex flex-column align-items-center ${['text', 'number', 'time'].includes(answerType ?? '') ? 'short-answer' : 'long-answer'}`}>
                  <h2 className={'fw-bold text-black'}>
                    {currentFormIndex + 1}/{(formQuestions ?? []).length}
                  </h2>

                  <h2 className={'fw-bold text-black'}>
                    {GetResourceValue(
                      state.gameDocument!,
                      formQuestions[currentFormIndex].questionResId ?? ''
                    )}
                  </h2>

                  {formMedia.length > 0 && (
                    <div className={'d-flex justify-content-center mb-1'}>
                      <Gallery
                        className={'task-content-media'}
                        items={formMedia}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div
                id={'task-content-answer'}
                className={`d-flex flex-column flex-grow-1`}>
                <Answer
                  key={currentFormIndex}
                  form={formQuestions[currentFormIndex]}
                />
              </div>
            </div>

            <div className={'content__footer'}>
              <div className={'content border-top'}>
                <div
                  className={`align-items-center d-flex justify-content-${currentFormIndex > 0 ? 'between' : 'end'}`}>
                  {currentFormIndex > 0 && (
                    <Button
                      className={'fw-bold'}
                      fillMode={'flat'}
                      onClick={() => handleNavigation('prev')}>
                      <span className={'d-flex align-items-center'}>
                        <span className={'material-symbols-outlined'}>
                          navigate_before
                        </span>
                        <span>Previous</span>
                      </span>
                    </Button>
                  )}

                  <Button
                    className={'fw-bold'}
                    fillMode={'flat'}
                    onClick={() =>
                      formQuestions.length - 1 === currentFormIndex
                        ? toggle()
                        : handleNavigation('next')
                    }>
                    <span className={'d-flex align-items-center'}>
                      <span>
                        {formQuestions.length - 1 === currentFormIndex
                          ? 'Close'
                          : 'Next'}
                      </span>
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </Container>
    </div>
  ) : null;
};
